import api from './api';

export interface LiveSession {
  id: string;
  user_id: string;
  platform: 'facebook' | 'instagram' | 'youtube' | 'tiktok';
  platform_live_id?: string;
  title?: string;
  status: 'configuring' | 'monitoring' | 'ended' | 'paused';
  social_account_id?: string;
  keyword_rules: KeywordRule[];
  marketplace_store_slug?: string;
  marketplace_api_url?: string;
  settings: LiveSettings;
  stats: LiveStats;
  started_at?: string;
  ended_at?: string;
  created_at: string;
  updated_at: string;
}

export interface KeywordRule {
  keyword: string;
  match_type: 'exact' | 'contains';
  action: 'workflow' | 'capture_only';
  workflow_id?: string;
  workflow_name?: string;
  product_name?: string;
  product_url?: string;
  product_image?: string;
  coupon_code?: string;
}

export interface LiveSettings {
  auto_detect: boolean;
  response_delay_ms?: number;
  max_responses_per_minute?: number;
  tiktok_username?: string;
  live_chat_id?: string;
}

export interface LiveStats {
  total_comments: number;
  total_captures: number;
  total_dms_sent: number;
}

export interface LiveContact {
  id: string;
  live_session_id: string;
  platform: string;
  platform_user_id: string;
  platform_username: string;
  display_name: string;
  comment_text: string;
  matched_keyword: string;
  action_taken: 'workflow_triggered' | 'captured_only' | 'failed';
  product_url?: string;
  coupon_code?: string;
  converted: boolean;
  captured_at: string;
}

export interface DetectedLive {
  platform: string;
  live_id: string;
  title: string;
  viewer_count?: number;
  started_at?: string;
  account_name: string;
}

export const liveAutomationService = {
  // Sessions
  async getSessions(params?: { page?: number; limit?: number; status?: string; platform?: string }) {
    const response = await api.get('/live-automation/sessions', { params });
    return response.data;
  },

  async getSession(id: string) {
    const response = await api.get(`/live-automation/sessions/${id}`);
    return response.data;
  },

  async createSession(data: Partial<LiveSession>) {
    const response = await api.post('/live-automation/sessions', data);
    return response.data;
  },

  async updateSession(id: string, data: Partial<LiveSession>) {
    const response = await api.put(`/live-automation/sessions/${id}`, data);
    return response.data;
  },

  async deleteSession(id: string) {
    const response = await api.delete(`/live-automation/sessions/${id}`);
    return response.data;
  },

  async startMonitoring(id: string) {
    const response = await api.post(`/live-automation/sessions/${id}/start`);
    return response.data;
  },

  async stopMonitoring(id: string) {
    const response = await api.post(`/live-automation/sessions/${id}/stop`);
    return response.data;
  },

  // Contacts
  async getContacts(sessionId: string, params?: { page?: number; limit?: number }) {
    const response = await api.get(`/live-automation/sessions/${sessionId}/contacts`, { params });
    return response.data;
  },

  async notifyAll(sessionId: string, message: string) {
    const response = await api.post(`/live-automation/sessions/${sessionId}/notify-all`, { message });
    return response.data;
  },

  // Stats
  async getStats(sessionId: string) {
    const response = await api.get(`/live-automation/sessions/${sessionId}/stats`);
    return response.data;
  },

  // Detection
  async detectLives() {
    const response = await api.get('/live-automation/detect-lives');
    return response.data;
  },

  // Get all contacts (for CSV export)
  async getAllContacts(sessionId: string) {
    const response = await api.get(`/live-automation/sessions/${sessionId}/contacts`, { params: { limit: 10000 } });
    return response.data;
  },

  // Test keyword
  async testKeyword(sessionId: string, text: string) {
    const response = await api.post(`/live-automation/sessions/${sessionId}/test-keyword`, { text });
    return response.data;
  },

  // Marketplace products
  async getMarketplaceProducts(storeSlug: string, search?: string) {
    const response = await api.get('/live-automation/marketplace/products', { params: { store_slug: storeSlug, search } });
    return response.data;
  }
};
